// Rest… for a runner (qa-runs#1171 part 3; the server half is vtt/server/src/rolledRests.ts): a token's menu offers
// Rest… where the system's healing page has rests that roll (Shadowrun 3's Stun recovery and Physical healing, by its
// data, never by name here). The dialog chooses one; a stage-by-stage healing may add dice (an adept's Rapid Healing,
// +1 a level — the person's own number, the server caps it); the server throws the tests, writes the boxes back and
// logs the line, and the dialog shows that line: *Stun: 4 boxes back over 2 hours*. Who: the player for their own
// character, the DM for any (the server decides). Touch-friendly: the dialog's buttons are finger-sized on a tablet.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { useTableExtras, type RestView } from '../../tableExtras';
import { BTN } from '../ui';

const btn = `${BTN} min-h-[2rem] [@media(pointer:coarse)]:min-h-[2.75rem] [@media(pointer:coarse)]:px-3`;
const item = 'flex w-full items-center gap-2 px-3 py-1 text-left text-sm hover:bg-stone-800 focus:bg-stone-800 focus:outline-none disabled:opacity-50';

/** Which token's Rest… dialog is open, and where (the menu's point). */
interface Open { id: string; x: number; y: number }
let open: Open | null = null;
const listeners = new Set<() => void>();
export const rolledRest = {
  get: () => open,
  open(o: Open) { open = o; for (const l of listeners) l(); },
  close() { if (open) { open = null; for (const l of listeners) l(); } },
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },
};

/** The token menu's item: shown only where the system has rests that roll. `onPick` closes the menu. */
export function RolledRestItem({ id, at, onPick }: { id: string; at: { x: number; y: number }; onPick: () => void }) {
  const { campaignId } = useShell();
  const { rests } = useTableExtras(campaignId);
  if (!rests.length) return null;
  return <button type="button" role="menuitem" className={item} data-testid="rolled-rest-item" onClick={() => { onPick(); rolledRest.open({ id, x: at.x, y: at.y }); }}>Rest…</button>;
}

/** The dialog, mounted once over the board. */
export function RolledRestDialog() {
  const at = useSyncExternalStore(rolledRest.subscribe, rolledRest.get);
  const { store, act, setNotice, log, meId } = useTableState();
  const { campaignId } = useShell();
  const { rests } = useTableExtras(campaignId);
  const [chosen, setChosen] = useState<string>('');
  const [extra, setExtra] = useState(0);
  const [boxes, setBoxes] = useState('');
  const [sent, setSent] = useState<{ label: string; before: Set<string> } | null>(null);
  useEffect(() => { setSent(null); setExtra(0); setBoxes(''); setChosen(rests[0]?.key ?? ''); }, [at?.id, rests]);
  if (!at) return null;
  const o = (store.state.objects as Record<string, { kind: string; name?: string } | undefined>)[at.id];
  if (!o || o.kind !== 'token') return null;
  const rest: RestView | undefined = rests.find((r) => r.key === chosen);
  // The line the server logged for this rest: a roll of mine that was not in the log when it was sent, naming the rest.
  const result = sent ? log.map((e) => (e.kind === 'roll' ? e.roll : null)).find((r) => r && !sent.before.has(r.id) && r.actor.id === meId && r.label?.includes(sent.label)) ?? null : null;
  const send = () => {
    if (!rest) return;
    const n = Number(boxes);
    setSent({ label: rest.label, before: new Set(log.flatMap((e) => (e.kind === 'roll' ? [e.roll.id] : []))) });
    act({ kind: 'rest.rolled', token: at.id, rest: rest.key, ...(rest.shape === 'per_box' && Number.isInteger(n) && n >= 1 ? { boxes: n } : {}), ...(rest.extraDice && extra > 0 ? { extraDice: extra } : {}) } as never,
      (r) => { if (!r.ok) { setSent(null); setNotice(r.reason ?? 'That rest could not be made.'); } });
  };
  const W = 288;
  const left = Math.max(4, Math.min(at.x, window.innerWidth - W - 4));
  const top = Math.max(4, Math.min(at.y, window.innerHeight - 260));
  return (
    <div role="dialog" aria-label={`Rest: ${o.name ?? 'token'}`} data-testid="rolled-rest-dialog" className="fixed z-50 vtt-popover p-3 text-sm shadow-xl" style={{ left, top, width: W }}>
      <h3 className="vtt-title truncate">Rest: {o.name ?? 'Token'}</h3>
      <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Which rest">
        {rests.map((r) => (
          <button key={r.key} type="button" role="radio" aria-checked={r.key === chosen} className={`${btn} ${r.key === chosen ? 'border-amber-500 text-amber-200' : ''}`} onClick={() => { setChosen(r.key); setSent(null); }}>{r.label}</button>
        ))}
      </div>
      {rest?.shape === 'per_box' && (
        <label className="mt-2 flex items-center gap-2 text-xs">Boxes back
          <input type="number" min={1} max={100} inputMode="numeric" placeholder="all" className="w-16 rounded bg-stone-800 px-1 py-1" value={boxes} onChange={(e) => setBoxes(e.target.value)} />
        </label>
      )}
      {rest?.extraDice && (
        <div className="mt-2 flex items-center gap-2 text-xs" data-testid="rest-extra-dice">
          <span title="Dice added to the healing test, such as an adept's Rapid Healing (+1 a level).">Extra dice (Rapid Healing)</span>
          <button type="button" className={btn} aria-label="One fewer extra die" disabled={extra <= 0} onClick={() => setExtra((n) => Math.max(0, n - 1))}>−</button>
          <span className="w-5 text-center" aria-live="polite">{extra}</span>
          <button type="button" className={btn} aria-label="One more extra die" disabled={extra >= 12} onClick={() => setExtra((n) => Math.min(12, n + 1))}>+</button>
        </div>
      )}
      {sent && <p className="mt-2 text-amber-200" data-testid="rolled-rest-result" aria-live="polite">{result ? result.label : 'Rolling…'}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className={btn} onClick={() => rolledRest.close()}>Close</button>
        <button type="button" className={`${btn} border-amber-600`} disabled={!rest || (sent !== null && !result)} onClick={send}>{sent && result ? 'Rest again' : 'Rest'}</button>
      </div>
    </div>
  );
}
